import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { FiPlus, FiFileText, FiFile, FiEdit, FiTrash2, FiDownload, FiBook } from 'react-icons/fi';
import { materialsAPI, API_ORIGIN } from '../../../services/api';
import './MaterialsPage.css';

const MaterialsPage = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const canEdit = user?.role === 'admin';
    const [materials, setMaterials] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [selectedMaterial, setSelectedMaterial] = useState(null);

    useEffect(() => {
        loadMaterials();
    }, []);

    const loadMaterials = async () => {
        try {
            setLoading(true);
            const response = await materialsAPI.getMaterials();
            setMaterials(response.data.materials || []);
        } catch (error) {
            console.error('Ошибка загрузки материалов:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id, title) => {
        if (!window.confirm(`Вы уверены, что хотите удалить материал "${title}"?`)) {
            return;
        }

        try {
            await materialsAPI.deleteMaterial(id);
            setMaterials(materials.filter(m => m.id !== id));
        } catch (error) {
            console.error('Ошибка удаления материала:', error);
            alert('Ошибка при удалении материала');
        }
    };

    const filteredMaterials = filter === 'all' 
        ? materials 
        : materials.filter(m => m.type === filter);

    const openMaterial = (material) => {
        if (material.type === 'lesson' && material.lesson_id) {
            navigate(`/admin/lessons/${material.lesson_id}/view`);
            return;
        }

        setSelectedMaterial(material);
    };

    if (loading) {
        return <div className="materials-loading">Загрузка...</div>;
    }

    if (selectedMaterial) {
        return (
            <div className="materials-page">
                <div className="material-detail-admin">
                    <div className="material-detail-admin-header">
                        <button
                            type="button"
                            onClick={() => setSelectedMaterial(null)}
                            className="btn-back-mat"
                        >
                            ← Назад к списку
                        </button>
                        <div>
                            <h1>{selectedMaterial.title}</h1>
                            <div className="material-detail-admin-meta">
                                <span>
                                    {selectedMaterial.type === 'text'
                                        ? 'Текстовый материал'
                                        : selectedMaterial.type === 'lesson'
                                          ? 'Урок-материал'
                                          : 'Файл'}
                                </span>
                                <span>{new Date(selectedMaterial.created_at).toLocaleDateString('ru-RU')}</span>
                            </div>
                        </div>
                    </div>

                    {selectedMaterial.description && (
                        <div className="material-detail-admin-description">
                            {selectedMaterial.description}
                        </div>
                    )}

                    {selectedMaterial.type === 'text' && (
                        <div
                            className="material-detail-admin-content"
                            dangerouslySetInnerHTML={{ __html: selectedMaterial.content || '' }}
                        />
                    )}

                    {selectedMaterial.type === 'file' && selectedMaterial.file_url && (
                        <div className="material-detail-admin-file">
                            <FiFile size={48} color="#52c41a" />
                            <p>Файл материала</p>
                            <a
                                href={`${API_ORIGIN}${selectedMaterial.file_url}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn-download-file-admin"
                            >
                                <FiDownload />
                                Открыть файл
                            </a>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="materials-page">
            <div className="materials-header">
                <div>
                    <h1>Материалы</h1>
                    <p>Дополнительные материалы для студентов</p>
                </div>
                {canEdit && (
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        <button
                            type="button"
                            onClick={() => navigate('/admin/materials/create')}
                            className="btn-create-material"
                        >
                            <FiPlus />
                            Добавить материал
                        </button>
                        <button
                            type="button"
                            onClick={() => navigate('/admin/materials/create-lesson')}
                            className="btn-create-material"
                            style={{ background: '#52c41a' }}
                        >
                            <FiPlus />
                            Создать урок-материал
                        </button>
                    </div>
                )}
            </div>

            {/* Фильтры */}
            <div className="materials-filters">
                <button 
                    className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
                    onClick={() => setFilter('all')}
                >
                    Все
                </button>
                <button 
                    className={`filter-btn ${filter === 'text' ? 'active' : ''}`}
                    onClick={() => setFilter('text')}
                >
                    <FiFileText />
                    Текстовые
                </button>
                <button 
                    className={`filter-btn ${filter === 'file' ? 'active' : ''}`}
                    onClick={() => setFilter('file')}
                >
                    <FiFile />
                    Файлы
                </button>
                <button 
                    className={`filter-btn ${filter === 'lesson' ? 'active' : ''}`}
                    onClick={() => setFilter('lesson')}
                >
                    <FiBook />
                    Уроки
                </button>
            </div>

            {filteredMaterials.length === 0 ? (
                <div className="empty-materials">
                    <p>Материалов пока нет</p>
                    {canEdit && (
                        <button
                            type="button"
                            onClick={() => navigate('/admin/materials/create')}
                            className="btn-create-empty"
                        >
                            <FiPlus />
                            Добавить первый материал
                        </button>
                    )}
                </div>
            ) : (
                <div className="materials-list">
                    {filteredMaterials.map(material => (
                        <div
                            key={material.id}
                            className="material-card"
                            onClick={() => openMaterial(material)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    openMaterial(material);
                                }
                            }}
                        >
                            <div className="material-icon">
                                {material.type === 'text' ? (
                                    <FiFileText size={32} color="#4a90e2" />
                                ) : material.type === 'lesson' ? (
                                    <FiBook size={32} color="#4a90e2" />
                                ) : (
                                    <FiFile size={32} color="#52c41a" />
                                )}
                            </div>
                            
                            <div className="material-info">
                                <h3>{material.title}</h3>
                                {material.description && (
                                    <p className="material-description">{material.description}</p>
                                )}
                                <div className="material-meta">
                                    <span className="material-type">
                                        {material.type === 'text' ? 'Текстовый материал' : material.type === 'lesson' ? 'Урок-материал' : 'Файл'}
                                    </span>
                                    <span className="material-date">
                                        {new Date(material.created_at).toLocaleDateString('ru-RU')}
                                    </span>
                                </div>
                            </div>

                            <div className="material-actions">
                                {material.type === 'file' && material.file_url && (
                                    <button
                                        type="button"
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            window.open(`${API_ORIGIN}${material.file_url}`, '_blank');
                                        }}
                                        className="btn-download"
                                        title="Скачать"
                                    >
                                        <FiDownload />
                                    </button>
                                )}
                                {canEdit && (
                                    <>
                                        <button
                                            type="button"
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                if (material.type === 'lesson') {
                                                    navigate(`/admin/materials/${material.id}/lesson/${material.lesson_id}/edit-lesson-material`);
                                                } else {
                                                    navigate(`/admin/materials/${material.id}/edit`);
                                                }
                                            }}
                                            className="btn-edit-mat"
                                            title="Редактировать"
                                        >
                                            <FiEdit />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                handleDelete(material.id, material.title);
                                            }}
                                            className="btn-delete-mat"
                                            title="Удалить"
                                        >
                                            <FiTrash2 />
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default MaterialsPage;
